import { Box } from "@mui/system";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RegListCard from "./RegListCard/RegListCard";
import style from "./RegistrationList.module.css";
import {
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import emptyWorkshopImage from "../../../images/emptyDesks.jpg";
import RegisterUser from "./RegisterUser/RegisterUser";
import {
  declineOfferedSeat,
  joinWorkshopWaitlist,
  leaveWorkshopWaitlist,
  registerForWorkshop,
  unRegisterFromWorkshop,
  registerGroupForWorkshop,
} from "../../../state/workshopSlice";
import EmailIcon from "@mui/icons-material/Email";
import EmailRegisteredUsers from "./EmailRegisteredUsers/EmailRegisteredUsers";
import Groups2Icon from "@mui/icons-material/Groups2";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import RemindRegisteredUsers from "./RemindRegisteredUsers/RemindRegisteredUsers";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { CSVLink } from "react-csv";
import { DateTime } from "luxon";
import { useReactToPrint } from "react-to-print";
import PrintRegistrationList from "./PrintRegistrationList";
import useIsAdmin from "../../../hooks/useIsAdmin";
import RegisterGroup from "./RegisterGroup/RegisterGroup";
import useCanViewWorkshop from "../../../hooks/useCanViewWorkshop";

const RegistrationList = () => {
  const dispatch = useDispatch();
  const isAdmin = useIsAdmin();
  const canView = useCanViewWorkshop();
  const workshop = useSelector((state) => state.workshops.selectedWorkshop);
  const { workshopSaving, workshopLoading } = useSelector(
    (state) => state.workshops.status
  );
  //get paramaters from url
  const [searchParams, setSearchParams] = useSearchParams({});
  //toggle to show add single user control
  const [showAddUser, setShowAddUser] = useState(false);
  //toggle to show add group control
  const [showAddGroup, setShowAddGroup] = useState(false);
  //toggle to show send mail control
  const [showMail, setShowMail] = useState(false);
  //toggle to show send reminder control
  const [showRemind, setShowRemind] = useState(false);

  const [csvData, setCSVData] = useState([]);
  const [emailOnRemove, setEmailOnRemove] = useState(true);
  const [workshopIsEmpty, setWorkshopIsEmpty] = useState(true);
  const [workshopIsFull, setWorkshopIsFull] = useState(false);
  const [registrations, setRegistrations] = useState({
    registrations: [],
    waitlist: [],
  });

  const printComponentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });
  //csvHeaders are only used for exporting reg list to csv in buildCSVData()
  const csvHeaders = [
    { label: "", key: "r1" },
    { label: "", key: "r2" },
    { label: "", key: "r3" },
    { label: "", key: "r4" },
    { label: "", key: "r5" },
    { label: "", key: "r6" },
  ];

  useEffect(() => {
    searchParams.get("add") === "true"
      ? setShowAddUser(true)
      : setShowAddUser(false);
    searchParams.get("addgroup") === "true"
      ? setShowAddGroup(true)
      : setShowAddGroup(false);
    searchParams.get("mail") === "true"
      ? setShowMail(true)
      : setShowMail(false);
    searchParams.get("remind") === "true"
      ? setShowRemind(true)
      : setShowRemind(false);
  }, [searchParams]);

  //get a variable to see if the workshop is empty
  useEffect(() => {
    workshop?.seats?.actualNumberRegistered > 0
      ? setWorkshopIsEmpty(false)
      : setWorkshopIsEmpty(true);

    workshop?.seats?.emptySeats <= 0
      ? setWorkshopIsFull(true)
      : setWorkshopIsFull(false);

    //sort registration list
    let registrations = undefined;
    if (workshop?.registrations !== undefined) {
      registrations = [...workshop.registrations];
      registrations.sort(orderRegistrations);
    }

    //sort waitlist
    let waitlist = undefined;
    if (workshop?.waitlists !== undefined) {
      waitlist = [...workshop.waitlists];
      waitlist.sort(orderWaitlists);
    }

    setRegistrations({
      registrations: registrations,
      waitlist: waitlist,
    });

    if (workshop) buildCSVData(workshop, registrations);
  }, [workshop]);

  //build registration list in a format to be used by downloadable CSV
  const buildCSVData = (workshop, registrations) => {
    let registrationListForCSV = [
      { r1: workshop.title, r2: "", r3: "", r4: "", r5: "", r6: "" },
      {
        r1: DateTime.fromISO(workshop?.startDatetime).toLocaleString(
          DateTime.DATETIME_MED
        ),
        r2: "",
        r3: "",
        r4: "",
        r5: "",
        r6: "",
      },
      {
        r1: `${registrations.length} people registered`,
        r2: "",
        r3: "",
        r4: "",
        r5: "",
        r6: "",
      },
      {},
      {
        r1: "First Name",
        r2: "Last Name",
        r3: "Email",
        r4: "Location",
        r5: "Job Title",
        r6: "Attended",
      },
    ];
    registrationListForCSV = registrationListForCSV.concat(
      registrations?.map((reg) => {
        return {
          r1: reg.staff.legalFirstname,
          r2: reg.staff.legalSurname,
          r3: reg.staff.mail,
          r4: reg.staff.department,
          r5: reg.staff.jobTitle,
          r6: reg.attended ? "Yes" : "No/Unknown",
        };
      })
    );
    setCSVData(registrationListForCSV);
  };

  //function used for sorting the waitlist
  const orderWaitlists = (a, b) => {
    if (a?.offerExpiredDatetime !== null && b?.offerExpiredDatetime == null)
      return 1;
    if (a?.offerExpiredDatetime === null && b?.offerExpiredDatetime !== null)
      return -1;

    if (a?.offeredSeatDatetime !== null && b?.offeredSeatDatetime === null)
      return -1;
    if (a?.offeredSeatDatetime === null && b?.offeredSeatDatetime !== null)
      return 1;

    if (a?.offeredSeatDatetime < b?.offeredSeatDatetime) return -1;
    if (a?.offeredSeatDatetime > b?.offeredSeatDatetime) return 1;
    if (a.joinedWaitlistDatetime < b.joinedWaitlistDatetime) return -1;
    if (a.joinedWaitlistDatetime > b.joinedWaitlistDatetime) return 1;

    return 0;
  };

  const orderRegistrations = (a, b) => {
    if (a?.registeredDatetime > b?.registeredDatetime) return -1;
    if (a?.registeredDatetime < b?.registeredDatetime) return 1;
    return 0;
  };

  //register a single user
  const registerUser = (mail, sendMail, addEvent) => {
    dispatch(
      registerForWorkshop(workshop.workshopId, mail, sendMail, addEvent)
    );
    setRegistrations({ registrations: [], waitlist: [] });
  };

  //register a group of users
  const registerGroup = (mails, sendMail, addEvent) => {
    dispatch(
      registerGroupForWorkshop(workshop.workshopId, mails, sendMail, addEvent)
    );
    //setRegistrations({ registrations: [], waitlist: [] });
  };

  const acceptSeat = (mail, sendMail, addEvent) => {
    dispatch(
      registerForWorkshop(workshop.workshopId, mail, sendMail, addEvent, true)
    );
    setRegistrations({ registrations: [], waitlist: [] });
  };

  const declineSeat = (mail, sendMail) => {
    dispatch(declineOfferedSeat(workshop.workshopId, mail, sendMail));
  };

  const joinWaitlist = (mail, sendMail) => {
    dispatch(joinWorkshopWaitlist(workshop.workshopId, mail, sendMail));
    setRegistrations({ registrations: [], waitlist: [] });
  };

  const unRegister = (mail) => {
    dispatch(unRegisterFromWorkshop(workshop.workshopId, mail, emailOnRemove));
    setRegistrations({ registrations: [], waitlist: [] });
  };

  const leaveWaitlist = (mail) => {
    dispatch(leaveWorkshopWaitlist(workshop.workshopId, mail, emailOnRemove));
    setRegistrations({ registrations: [], waitlist: [] });
  };

  const exportCSV = () => {};

  return (
    <>
      {canView && (
        <Box
          className={`${style.attendees} ${workshopIsEmpty && style.emptyRoom}`}
          sx={{
            backgroundImage: workshopIsEmpty && `url(${emptyWorkshopImage})`,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              backgroundColor: "primary.light",
              color: "specialHeadingText.main",
              padding: "1rem 1rem 0.5rem 1rem",
              boxSizing: "border-box",
            }}
          >
            {workshop && (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                    {workshop?.title}
                  </Typography>
                  <Typography variant="h6" sx={{ marginTop: "0.2rem" }}>
                    Manage attendance
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2">
                    Capacity: {workshop.capacity}
                  </Typography>
                  <Typography variant="body2">
                    Registered: {workshop.seats.numberRegistered}
                  </Typography>
                  <Typography variant="body2">
                    Empty seats:{" "}
                    {workshop.capacity - workshop.seats.numberRegistered}
                  </Typography>
                </Box>
              </Box>
            )}
            {/* List controls section */}
            <Box
              sx={{
                display: "flex",
                marginTop: "0.5rem",
                backgroundColor: "white",
                borderRadius: "6px",
              }}
            >
              {isAdmin && (
                <>
                  <Tooltip title="Register on behalf of user">
                    <IconButton
                      color={showAddUser ? "default" : "secondary"}
                      aria-label="Register on behalf of user"
                      onClick={() => {
                        setSearchParams(`add=${!showAddUser}`);
                      }}
                    >
                      <PersonAddAlt1Icon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Register group of users">
                    <IconButton
                      color={showAddGroup ? "default" : "secondary"}
                      aria-label="Register group of users"
                      onClick={() => {
                        setSearchParams(`addgroup=${!showAddGroup}`);
                      }}
                    >
                      <GroupAddIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Send a message to people signed up for this workshop">
                <IconButton
                  color={showMail ? "default" : "secondary"}
                  aria-label="Email people on the registration list"
                  onClick={() => {
                    setSearchParams(`mail=${!showMail}`);
                  }}
                >
                  <EmailIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remind registered users about this workshop">
                <IconButton
                  color={showRemind ? "default" : "secondary"}
                  aria-label="Remind registered users about this workshop"
                  onClick={() => {
                    setSearchParams(`remind=${!showRemind}`);
                  }}
                >
                  <NotificationsActiveIcon />
                </IconButton>
              </Tooltip>
              {csvData && workshop && (
                <CSVLink
                  data={csvData}
                  headers={csvHeaders}
                  filename={workshop.title}
                >
                  <Tooltip title="Export to Excel">
                    <IconButton
                      color={"secondary"}
                      aria-label="Export to Excel"
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Tooltip>
                </CSVLink>
              )}
              <Tooltip title="Print sign-in sheet">
                <IconButton
                  color={"secondary"}
                  aria-label="Print sign-in sheet"
                  onClick={handlePrint}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <Box style={{ display: "none" }}>
                <Box ref={printComponentRef}>
                  <PrintRegistrationList workshop={workshop} />
                </Box>
              </Box>
              {isAdmin && (
                <Tooltip title="Turn this off if you do not want an email sent to people when they are removed from registration or waitlist">
                  <FormControlLabel
                    sx={{ marginLeft: "auto", marginRight: "0px" }}
                    control={
                      <Switch
                        checked={emailOnRemove}
                        onChange={(e) => {
                          setEmailOnRemove(e.target.checked);
                        }}
                      />
                    }
                    label="Email on remove"
                    labelPlacement="start"
                  />
                </Tooltip>
              )}
            </Box>
          </Paper>

          {/* Action cards */}
          {isAdmin && (
            <>
              <RegisterUser
                workshopIsFull={workshopIsFull}
                registerUser={registerUser}
                joinWaitlist={joinWaitlist}
                show={showAddUser}
              />
              <RegisterGroup
                workshopIsFull={workshopIsFull}
                show={showAddGroup}
                registerGroup={registerGroup}
              />
            </>
          )}
          <EmailRegisteredUsers workshop={workshop} show={showMail} />
          <RemindRegisteredUsers workshop={workshop} show={showRemind} />

          {/* Lists */}
          {workshop && !workshopSaving && !workshopLoading && (
            <>
              <Box className={style.registrationList}>
                <Box className={style.heading}>
                  <Groups2Icon fontSize="medium" />
                  <Typography variant="h4">
                    Registered ({registrations.registrations?.length})
                  </Typography>
                </Box>
                {/*------ Registration Cards -------*/}
                {workshop &&
                  registrations.registrations?.map((r, index) => (
                    <RegListCard
                      key={index}
                      registration={r}
                      className={style.listCard}
                      unRegister={unRegister}
                    />
                  ))}
                {workshop && workshop.registrations?.length === 0 && (
                  <Box sx={{ textAlign: "center", margin: "2rem 0" }}>
                    <Typography variant="h4" color="primary">
                      No one has registered yet
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box className={style.registrationList}>
                <Box className={style.heading}>
                  <AccessTimeIcon fontSize="medium" />
                  <Typography variant="h4">
                    Waitlist ({registrations.waitlist?.length})
                  </Typography>
                </Box>
                {/*---------- Waitlist Cards -------------*/}
                {workshop &&
                  registrations.waitlist?.map((r, index) => (
                    <RegListCard
                      key={index}
                      registration={r}
                      isWaitlist
                      className={style.listCard}
                      leaveWaitlist={leaveWaitlist}
                      acceptSeat={acceptSeat}
                      declineSeat={declineSeat}
                    />
                  ))}
                {workshop && workshop.waitlists?.length === 0 && (
                  <Box sx={{ textAlign: "center", marginTop: "2rem" }}>
                    <Typography variant="h4" color="primary">
                      The waitlist is empty
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      )}
      {!canView && (
        <Paper
          sx={{
            margin: "1rem 0.5rem",
            padding: "1rem",
            backgroundColor: "rgba(207, 207, 211, 0.122)",
          }}
        >
          <Box>
            <Typography variant="h3">
              Sorry you do not have access to this workshop
            </Typography>
          </Box>
        </Paper>
      )}
    </>
  );
};

export default RegistrationList;
